/**
 * Modern browser-compatible encryption using Web Crypto API
 * Replaces XXTEA encryption with AES-GCM
 */

// Convert string to Uint8Array
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

// Derive key from app ID
async function deriveKey(appId: string): Promise<CryptoKey> {
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    textEncoder.encode(appId.padEnd(32, '0').substring(0, 32)),
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: textEncoder.encode('todesktop-salt'),
      iterations: 1000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function encryptToBase64(
  data: string,
  appId: string,
): Promise<string> {
  const key = await deriveKey(appId);
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    textEncoder.encode(data),
  );

  // Combine IV and encrypted data
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);

  // Convert to base64
  return btoa(String.fromCharCode(...combined));
}

export async function decryptFromBase64(
  encryptedData: string,
  appId: string,
): Promise<string> {
  const key = await deriveKey(appId);

  // Decode base64
  const combined = new Uint8Array(
    atob(encryptedData)
      .split('')
      .map((c) => c.charCodeAt(0)),
  );

  // Extract IV and encrypted data
  const iv = combined.slice(0, 12);
  const encrypted = combined.slice(12);

  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    encrypted,
  );

  return textDecoder.decode(decrypted);
}
